import { ADDRESS_STATUS_TYPE } from "@/constants";
import { ApplicationState, Asset, Customer } from "@/store/slices/types/applicationFormSlice";
import { getAssetFieldAnchorId } from "@/utils/commonFunctions";
import { getMissingRequiredFields, MissingRequiredField, RequiredFieldRule } from "@/utils/requiredFieldRules";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "@/components/Utils/helperFunction";
import { ASSET_FIELD } from "./assetRequiredFields";

interface InsuranceQuoteRequiredFieldSource {
  asset: Asset;
  customer?: Customer;
  entityId?: string;
  customerRole: string;
}

/** Customer fields anchor to the customer form, or to the "add customer" action when there is none yet. */
const getCustomerAnchorId = (
  fieldName: string,
  { customer, customerRole }: InsuranceQuoteRequiredFieldSource,
): string => (customer?._id ? `${fieldName}_${customer._id}` : `add${customerRole}`);

const INSURANCE_QUOTE_REQUIRED_FIELDS: RequiredFieldRule<InsuranceQuoteRequiredFieldSource>[] = [
  {
    key: ASSET_FIELD.assetValue,
    label: "asset value",
    getAnchorId: ({ asset }) => getAssetFieldAnchorId(ASSET_FIELD.assetValue, asset?._id),
    isSatisfied: ({ asset }) => Boolean(asset?.assetValue),
  },
  {
    key: ASSET_FIELD.assetType,
    label: "asset type",
    getAnchorId: ({ asset }) => getAssetFieldAnchorId(ASSET_FIELD.assetType, asset?._id),
    isSatisfied: ({ asset }) => Boolean(asset?.assetType),
  },
  {
    key: "entity",
    label: "entity",
    getAnchorId: () => "addEntity",
    isSatisfied: ({ entityId }) => Boolean(entityId),
  },
  {
    key: "firstName",
    label: "first name",
    getAnchorId: (source) => getCustomerAnchorId("firstName", source),
    isSatisfied: ({ customer }) => Boolean(customer?.firstName),
  },
  {
    key: "email",
    label: "email",
    getAnchorId: (source) => getCustomerAnchorId("email", source),
    isSatisfied: ({ customer }) => Boolean(customer?.emails?.[0]?.address),
  },
  {
    key: "phone",
    label: "mobile number",
    getAnchorId: (source) => getCustomerAnchorId("phoneNumber", source),
    isSatisfied: ({ customer }) => Boolean(customer?.phoneNumbers?.[0]?.number),
  },
  {
    key: "address",
    label: "current address",
    getAnchorId: (source) => getCustomerAnchorId("address", source),
    // The server quotes on the customer's current address and refuses a customer without one.
    isSatisfied: ({ customer }) =>
      Boolean(customer?.addresses?.some((address) => address?.status === ADDRESS_STATUS_TYPE.CURRENT)),
  },
];

interface InsuranceQuoteMissingFieldsParams {
  application: ApplicationState;
  asset: Asset;
  /** The application's first customer, who the quote is for. */
  customer?: Customer;
}

/** What the asset's insurance quote still needs. A quote is for the application's first entity and first customer. */
export const getInsuranceQuoteMissingFields = ({
  application,
  asset,
  customer,
}: InsuranceQuoteMissingFieldsParams): MissingRequiredField[] =>
  getMissingRequiredFields(INSURANCE_QUOTE_REQUIRED_FIELDS, {
    asset,
    customer,
    entityId: application.entities?.[0]?._id,
    customerRole: getCustomerRole(application.applicationType, application.entities, application.purpose),
  });
